'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import NiceModal from '@ebay/nice-modal-react';
import { type SubmitHandler } from 'react-hook-form';

import { routes } from '@workspace/routes';
import { FormProvider } from '@workspace/ui/components/form';
import { toast } from '@workspace/ui/components/sonner';
import { cn } from '@workspace/ui/lib/utils';

import { completeGenerator } from '~/actions/generator/complete-generator';
import { type GeneratorMetadata } from '~/components/generator/shared/generator-step-props';
import { StepIndicator } from '~/components/generator/shared/step-indicator';
import { WizardFooter } from '~/components/generator/shared/wizard-footer';
import { GeneratorBackgroundsStep } from '~/components/generator/steps/generator-backgrounds-step';
import { GeneratorModelsVariantsStep } from '~/components/generator/steps/generator-models-variants-step';
import { GeneratorOverviewStep } from '~/components/generator/steps/generator-overview-step';
import { GeneratorPosesStep } from '~/components/generator/steps/generator-poses-step';
import { GeneratorProductsStep } from '~/components/generator/steps/generator-products-step';
import { UpgradePlanDialog } from '~/components/organizations/slug/settings/organization/billing/upgrade-plan-dialog';
import { useZodForm } from '~/hooks/use-zod-form';
import {
  backgroundGeneratorSchema,
  completeGeneratorSchema,
  GeneratorStep,
  modelGeneratorSchema,
  poseGeneratorSchema,
  productGeneratorSchema,
  type CompleteGeneratorActionSchema,
  type CompleteGeneratorSchema
} from '~/schemas/generator/complete-generator-schema';

const components = {
  [GeneratorStep.Product]: GeneratorProductsStep,
  [GeneratorStep.Model]: GeneratorModelsVariantsStep,
  [GeneratorStep.Pose]: GeneratorPosesStep,
  [GeneratorStep.Background]: GeneratorBackgroundsStep,
  [GeneratorStep.Review]: GeneratorOverviewStep
} as const;

function validateStep(
  step: GeneratorStep,
  values: CompleteGeneratorSchema
): boolean {
  // If step is not in activeSteps, it's always valid
  if (!values.activeSteps.includes(step)) {
    return true;
  }

  switch (step) {
    case GeneratorStep.Product:
      return values.productStep
        ? productGeneratorSchema.safeParse(values.productStep).success
        : false;
    case GeneratorStep.Model:
      return values.modelStep
        ? modelGeneratorSchema.safeParse(values.modelStep).success
        : false;
    case GeneratorStep.Pose:
      return values.poseStep
        ? poseGeneratorSchema.safeParse(values.poseStep).success
        : false;
    case GeneratorStep.Background:
      return values.backgroundStep
        ? backgroundGeneratorSchema.safeParse(values.backgroundStep).success
        : false;
    case GeneratorStep.Review:
      return true;
  }
}

function handleScrollToTop(): void {
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }
}

export type GeneratorWizardProps = React.HtmlHTMLAttributes<HTMLFormElement> & {
  activeSteps: GeneratorStep[];
  metadata?: GeneratorMetadata;
  isEmbed?: boolean;
};

export function GeneratorWizard({
  activeSteps,
  metadata,
  className,
  isEmbed,
  ...other
}: GeneratorWizardProps): React.JSX.Element {
  const router = useRouter();
  const STORAGE_KEY = React.useMemo(() => 'generator_wizard_state', []);
  const [currentStep, setCurrentStep] = React.useState<GeneratorStep>(
    activeSteps[0]
  );
  const [furthestCompletedStepIndex, setFurthestCompletedStepIndex] =
    React.useState<number>(0);
  const [generatedImages, setGeneratedImages] = React.useState<string[]>([]);
  const [generationResult, setGenerationResult] = React.useState<any>(null);
  const methods = useZodForm({
    schema: completeGeneratorSchema,
    mode: 'all',
    defaultValues: {
      activeSteps,
      productStep: activeSteps.includes(GeneratorStep.Product)
        ? {
            products: metadata?.products ?? []
          }
        : undefined,
      modelStep: activeSteps.includes(GeneratorStep.Model)
        ? {
            modelVariants: metadata?.modelVariants ?? []
          }
        : undefined,
      poseStep: activeSteps.includes(GeneratorStep.Pose)
        ? {
            poses: metadata?.poses ?? []
          }
        : undefined,
      backgroundStep: activeSteps.includes(GeneratorStep.Background)
        ? {
            backgrounds: metadata?.backgrounds ?? []
          }
        : undefined
    }
  });
  const Component = components[currentStep];
  const currentStepIndex = activeSteps.indexOf(currentStep);
  const isLastStep = currentStepIndex === activeSteps.length - 1;
  const formValues = methods.getValues();
  const isCurrentStepValid = validateStep(currentStep, formValues);
  const canSubmit =
    !methods.formState.isSubmitting && methods.formState.isValid;

  // Calculate estimated credit cost
  const modelsCount = formValues.modelStep?.modelVariants?.length ?? 0;
  const posesCount = formValues.poseStep?.poses?.length ?? 0;
  const backgroundsCount = formValues.backgroundStep?.backgrounds?.length ?? 0;
  const estimatedCreditCost = modelsCount * posesCount * backgroundsCount;

  // Load saved state from sessionStorage on mount
  React.useEffect(() => {
    try {
      const raw =
        typeof window !== 'undefined'
          ? sessionStorage.getItem(STORAGE_KEY)
          : null;
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        currentStep?: GeneratorStep;
        furthestCompletedStepIndex?: number;
        formValues?: CompleteGeneratorSchema;
        generatedImages?: string[];
        generationResult?: any;
        activeSteps?: GeneratorStep[];
      } | null;
      if (!parsed) return;

      // Only restore if activeSteps match; otherwise ignore stale state
      if (
        Array.isArray(parsed.activeSteps) &&
        parsed.activeSteps.length === activeSteps.length &&
        parsed.activeSteps.every((s, i) => s === activeSteps[i]) &&
        parsed.formValues
      ) {
        methods.reset(parsed.formValues);
        if (parsed.currentStep && activeSteps.includes(parsed.currentStep)) {
          setCurrentStep(parsed.currentStep);
        }
        if (typeof parsed.furthestCompletedStepIndex === 'number') {
          setFurthestCompletedStepIndex(parsed.furthestCompletedStepIndex);
        }
        if (Array.isArray(parsed.generatedImages)) {
          setGeneratedImages(parsed.generatedImages);
        }
        if (parsed.generationResult !== undefined) {
          setGenerationResult(parsed.generationResult);
        }
      }
    } catch (err) {
      console.error(
        'Failed to restore generator state from sessionStorage',
        err
      );
    }
  }, []);

  // Persist state to sessionStorage whenever critical values change
  const persistState = React.useCallback(() => {
    try {
      if (typeof window === 'undefined') return;
      const toSave = {
        activeSteps,
        currentStep,
        furthestCompletedStepIndex,
        formValues: methods.getValues(),
        generatedImages,
        generationResult
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (err) {
      console.error('Failed to persist generator state to sessionStorage', err);
    }
  }, [
    STORAGE_KEY,
    activeSteps,
    currentStep,
    furthestCompletedStepIndex,
    methods,
    generatedImages,
    generationResult
  ]);

  // Watch form changes and persist (debounced via microtask to batch multiple changes per tick)
  React.useEffect(() => {
    const subscription = methods.watch(() => {
      Promise.resolve().then(persistState);
    });
    return () => subscription.unsubscribe();
  }, [methods, persistState]);

  // Persist when step-related or results change
  React.useEffect(() => {
    persistState();
  }, [
    currentStep,
    furthestCompletedStepIndex,
    generatedImages,
    generationResult,
    persistState
  ]);
  const onSubmit: SubmitHandler<CompleteGeneratorSchema> = async (values) => {
    if (!canSubmit || !isCurrentStepValid || !isLastStep) {
      return;
    }
    if (isEmbed) {
      window.open(routes.dashboard.Index, '_blank');
      return;
    }

    const { productStep, modelStep, poseStep, backgroundStep, ...rest } =
      values;

    const valuesToSubmit: CompleteGeneratorActionSchema = {
      productIds: productStep?.products?.map((item) => item.id) ?? [],
      modelVariantIds: modelStep?.modelVariants?.map((item) => item.id) ?? [],
      poseIds: poseStep?.poses?.map((item) => item.id) ?? [],
      backgroundIds: backgroundStep?.backgrounds?.map((item) => item.id) ?? []
    };

    const result = await completeGenerator(valuesToSubmit);
    setGenerationResult(result);
    persistState();

    // Handle server action codes for auth/billing flows
    if (result?.code === 'REQUIRE_AUTH') {
      const callbackUrl = routes.dashboard.Index;
      window.open(
        routes.dashboard.auth.SignIn + `?callbackUrl=${callbackUrl}`,
        '_blank'
      );
      return;
    }
    if (result?.code === 'INSUFFICIENT_CREDITS') {
      NiceModal.show(UpgradePlanDialog);
      return;
    }

    // Handle generation results with detailed error handling
    if (result?.data?.complete && result?.data?.successfulGenerations > 0) {
      setGeneratedImages(result?.data?.images ?? []);
      persistState();

      if (result?.code === 'PARTIAL_GENERATION') {
        toast.warning(
          `${result.data.successfulGenerations} images generated successfully, but ${result.data.failedGenerations} failed.`
        );
      } else {
        toast.success('All images generated successfully!');
      }

      // Clear persisted state after successful generation completion
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {}

      if (result?.data?.redirect) {
        router.push(result?.data?.redirect);
      }
    } else if (result?.code === 'GENERATION_FAILED') {
      toast.error(`Generation failed: ${result.error}`);
    } else if (result?.serverError || result?.validationErrors) {
      toast.error("Couldn't complete request");
    }
  };
  const handleNext = async (): Promise<void> => {
    if (!isCurrentStepValid) {
      return;
    }
    if (isLastStep) {
      methods.handleSubmit(onSubmit)();
      return;
    }
    // Unlock next step on successful validation
    setFurthestCompletedStepIndex((prev) =>
      Math.max(prev, currentStepIndex + 1)
    );
    setCurrentStep(activeSteps[currentStepIndex + 1]);
    handleScrollToTop();
  };

  // Keep track of the furthest completed step when the current step becomes valid
  React.useEffect(() => {
    if (isCurrentStepValid) {
      setFurthestCompletedStepIndex((prev) => Math.max(prev, currentStepIndex));
    }
  }, [isCurrentStepValid, currentStepIndex]);
  return (
    <FormProvider {...methods}>
      <div className={cn('mx-auto w-full min-w-80 space-y-4 px-4', className)}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="space-y-4"
          {...other}
        >
          <div className="flex justify-between pt-4">
            <div className="w-48 space-y-4">
              {/* <p className="text-sm text-muted-foreground">
              Step {currentStepIndex + 1} of {activeSteps.length}
            </p> */}
              <StepIndicator
                steps={activeSteps}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                furthestCompletedStepIndex={furthestCompletedStepIndex}
                validateStep={(step) => validateStep(step, formValues)}
              />
            </div>
            {/* If we wanted we could put this up top just once for all steps.
              <NextButton
              loading={methods.formState.isSubmitting}
              disabled={!isCurrentStepValid && !methods.formState.isSubmitting}
              isLastStep={isLastStep}
              onClick={handleNext}
            /> */}
          </div>

          {currentStep !== GeneratorStep.Review && (
            <Component metadata={metadata ?? {}} />
          )}

          {!isLastStep && (
            <WizardFooter
              canNext={isCurrentStepValid && !methods.formState.isSubmitting}
              loading={methods.formState.isSubmitting}
              isLastStep={isLastStep}
              handleNext={handleNext}
              currentStep={currentStep}
            />
          )}
        </form>

        {/* Results section outside the form */}
        {currentStep === GeneratorStep.Review && (
          <GeneratorOverviewStep
            // prop only used by review step
            generatedImages={generatedImages}
            generationResult={generationResult}
            onSubmit={methods.handleSubmit(onSubmit)}
            isSubmitting={methods.formState.isSubmitting}
            estimatedCreditCost={estimatedCreditCost}
          />
        )}
      </div>
    </FormProvider>
  );
}
